import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Customized
} from 'recharts';

// Zoom level configurations (in days)
const ZOOM_LEVELS = [
  { level: 1, window: null, label: 'Full' },       // Full data view
  { level: 2, window: 365, label: '1Y' },          // 1 year
  { level: 3, window: 90, label: 'Q' },            // Quarter (90 days)
  { level: 4, window: 30, label: '30d' },          // 30 days
  { level: 5, window: 14, label: '14d' },          // 14 days
  { level: 6, window: 7, label: '7d' },            // 7 days
  { level: 7, window: 3, label: '3d' }             // 3 days
];

const DAY_MS = 24 * 60 * 60 * 1000; // Milliseconds in a day

// Color palette for different stocks
const STOCK_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

/**
 * Timeline Scatter Chart with FIFO Pair Connection Lines
 * - Different colors for different stocks
 * - Lines connecting Entry → Exit pairs
 * - Solid dots for Entry, hollow for Exit
 */
const TimelineChart = ({ records, pairs = [] }) => {
  // State for interactive legend
  const [hoveredSymbol, setHoveredSymbol] = useState(null);
  const [focusedSymbols, setFocusedSymbols] = useState(new Set());

  // Zoom and pan state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [centerTime, setCenterTime] = useState(null);
  const chartRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartCenter, setDragStartCenter] = useState(null);

  // P&L filter state: 'all' | 'profit' | 'loss'
  const [plFilter, setPlFilter] = useState('all');

  // Calculate data time range
  const dataTimeRange = useMemo(() => {
    if (records.length === 0) return { min: Date.now(), max: Date.now() };
    const timestamps = records.map(r => new Date(r.timestamp).getTime());
    return {
      min: Math.min(...timestamps),
      max: Math.max(...timestamps)
    };
  }, [records]);

  // Get current view window based on zoom level
  const getViewWindow = useCallback(() => {
    const zoomConfig = ZOOM_LEVELS[zoomLevel - 1];
    const padding = DAY_MS * 0.5; // Half day padding

    if (zoomLevel === 1 || !zoomConfig.window) {
      return {
        start: dataTimeRange.min - padding,
        end: dataTimeRange.max + padding,
        window: dataTimeRange.max - dataTimeRange.min + padding * 2
      };
    }

    const windowMs = zoomConfig.window * DAY_MS;
    const center = centerTime || (dataTimeRange.min + dataTimeRange.max) / 2;
    const halfWindow = windowMs / 2;

    let start = center - halfWindow;
    let end = center + halfWindow;

    // Constrain to data bounds with padding
    const minBound = dataTimeRange.min - padding;
    const maxBound = dataTimeRange.max + padding;

    if (start < minBound) {
      start = minBound;
      end = Math.min(maxBound, start + windowMs);
    } else if (end > maxBound) {
      end = maxBound;
      start = Math.max(minBound, end - windowMs);
    }

    return { start, end, window: windowMs };
  }, [zoomLevel, centerTime, dataTimeRange]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const zoomIn = e.deltaY < 0;

    if (zoomIn && zoomLevel < 7) {
      const newLevel = zoomLevel + 1;
      setZoomLevel(newLevel);

      // Calculate where the mouse is pointing
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const chartWidth = rect.width - 90;
        const mousePercent = Math.max(0, Math.min(1, (mouseX - 60) / chartWidth));

        const view = getViewWindow();
        const mouseTime = view.start + mousePercent * (view.end - view.start);

        const newWindowMs = ZOOM_LEVELS[newLevel - 1].window * DAY_MS;
        const padding = DAY_MS * 0.5;
        const newCenter = Math.max(
          dataTimeRange.min - padding + newWindowMs / 2,
          Math.min(dataTimeRange.max + padding - newWindowMs / 2, mouseTime)
        );
        setCenterTime(newCenter);
      } else if (centerTime === null) {
        setCenterTime((dataTimeRange.min + dataTimeRange.max) / 2);
      }
    } else if (!zoomIn && zoomLevel > 1) {
      setZoomLevel(zoomLevel - 1);
      if (zoomLevel - 1 === 1) {
        setCenterTime(null);
      }
    }
  }, [zoomLevel, centerTime, getViewWindow, dataTimeRange]);

  // Handle drag start
  const handleMouseDown = useCallback((e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStartX(e.clientX);
      setDragStartCenter(centerTime || (dataTimeRange.min + dataTimeRange.max) / 2);
      e.preventDefault();
    }
  }, [zoomLevel, centerTime, dataTimeRange]);

  // Handle drag move
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || zoomLevel <= 1) return;

    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      const chartWidth = rect.width - 90;
      const deltaX = e.clientX - dragStartX;

      const view = getViewWindow();
      const timePerPixel = (view.end - view.start) / chartWidth;
      const deltaTime = -deltaX * timePerPixel;

      const zoomConfig = ZOOM_LEVELS[zoomLevel - 1];
      const windowMs = zoomConfig.window * DAY_MS;
      const padding = DAY_MS * 0.5;
      const halfWindow = windowMs / 2;

      const newCenter = Math.max(
        dataTimeRange.min - padding + halfWindow,
        Math.min(dataTimeRange.max + padding - halfWindow, dragStartCenter + deltaTime)
      );

      setCenterTime(newCenter);
    }
  }, [isDragging, zoomLevel, dragStartX, dragStartCenter, getViewWindow, dataTimeRange]);

  // Handle drag end
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse up listener for drag end
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging, handleMouseUp, handleMouseMove]);

  // Helper to check if a symbol should be dimmed
  const shouldDimSymbol = (symbol) => {
    // If hovering on a specific symbol, dim others
    if (hoveredSymbol && hoveredSymbol !== symbol) return true;
    // If there are focused symbols and this isn't one of them, dim it
    if (focusedSymbols.size > 0 && !focusedSymbols.has(symbol)) return true;
    return false;
  };

  // Get unique symbols and assign colors
  const symbolColors = useMemo(() => {
    const symbols = [...new Set(records.map(r => r.symbol))];
    const colorMap = {};
    symbols.forEach((symbol, index) => {
      colorMap[symbol] = STOCK_COLORS[index % STOCK_COLORS.length];
    });
    return colorMap;
  }, [records]);

  // Transform records for scatter chart
  const chartData = useMemo(() => {
    return records.map(record => ({
      timestamp: new Date(record.timestamp).getTime(),
      price: record.actualPrice,
      quantity: record.quantity,
      symbol: record.symbol,
      action: record.action,
      date: record.dateOnly,
      time: new Date(record.timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      color: symbolColors[record.symbol],
      id: record.id
    }));
  }, [records, symbolColors]);

  // Calculate FIFO pairs from records if pairs not provided
  const calculatedPairs = useMemo(() => {
    if (pairs && pairs.length > 0) {
      // Use provided pairs
      return pairs.map((pair, index) => {
        const entryTime = new Date(pair.entryTimestamp || pair.entry?.uiRecord?.timestamp).getTime();
        const exitTime = new Date(pair.exitTimestamp || pair.exit?.uiRecord?.timestamp).getTime();
        const entryPrice = pair.entryPrice || pair.entry?.uiRecord?.actualPrice;
        const exitPrice = pair.exitPrice || pair.exit?.uiRecord?.actualPrice;
        const symbol = pair.symbol;
        const quantity = pair.quantity || pair.entry?.uiRecord?.quantity || 0;
        const profit = pair.profit !== undefined ? pair.profit : (exitPrice - entryPrice) * quantity;

        return {
          id: `pair-${index}`,
          symbol,
          entryTime,
          exitTime,
          entryPrice,
          exitPrice,
          quantity,
          profit,
          color: symbolColors[symbol]
        };
      }).filter(p => !isNaN(p.entryTime) && !isNaN(p.exitTime));
    }

    // Calculate pairs from records using FIFO
    const entriesBySymbol = {};
    const exitsBySymbol = {};

    // Sort records by time
    const sortedRecords = [...records].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    sortedRecords.forEach(record => {
      const symbol = record.symbol;
      if (record.action === 'entry') {
        if (!entriesBySymbol[symbol]) entriesBySymbol[symbol] = [];
        entriesBySymbol[symbol].push(record);
      } else {
        if (!exitsBySymbol[symbol]) exitsBySymbol[symbol] = [];
        exitsBySymbol[symbol].push(record);
      }
    });

    const calculatedPairs = [];
    Object.keys(entriesBySymbol).forEach(symbol => {
      const entries = [...entriesBySymbol[symbol]];
      const exits = exitsBySymbol[symbol] || [];

      exits.forEach(exit => {
        if (entries.length > 0) {
          const entry = entries.shift(); // FIFO
          const entryTime = new Date(entry.timestamp).getTime();
          const exitTime = new Date(exit.timestamp).getTime();
          const quantity = Math.min(entry.quantity, exit.quantity);
          const profit = (exit.actualPrice - entry.actualPrice) * quantity;

          calculatedPairs.push({
            id: `calc-pair-${calculatedPairs.length}`,
            symbol,
            entryTime,
            exitTime,
            entryPrice: entry.actualPrice,
            exitPrice: exit.actualPrice,
            quantity,
            profit,
            color: symbolColors[symbol]
          });
        }
      });
    });

    return calculatedPairs;
  }, [pairs, records, symbolColors]);

  // Get zoom-adjusted time domain
  const viewWindow = getViewWindow();
  const displayMinTime = viewWindow.start;
  const displayMaxTime = viewWindow.end;

  // Filter pairs by P&L filter and zoom window
  const visiblePairs = useMemo(() => {
    let pairs = calculatedPairs;

    // Apply P&L filter
    if (plFilter === 'profit') {
      pairs = pairs.filter(p => p.profit > 0);
    } else if (plFilter === 'loss') {
      pairs = pairs.filter(p => p.profit < 0);
    }

    // Apply zoom window filter
    if (zoomLevel > 1) {
      pairs = pairs.filter(p =>
        (p.entryTime >= displayMinTime && p.entryTime <= displayMaxTime) ||
        (p.exitTime >= displayMinTime && p.exitTime <= displayMaxTime)
      );
    }

    return pairs;
  }, [calculatedPairs, plFilter, zoomLevel, displayMinTime, displayMaxTime]);

  // Get set of visible entry/exit times for filtering
  const visibleEntryTimes = useMemo(() => {
    return new Set(visiblePairs.map(p => p.entryTime));
  }, [visiblePairs]);

  // Separate entry and exit data, filtered by zoom window and P&L filter
  const entryData = useMemo(() => {
    let data = chartData.filter(d => d.action === 'entry');

    // Apply zoom window filter
    if (zoomLevel > 1) {
      data = data.filter(d => d.timestamp >= displayMinTime && d.timestamp <= displayMaxTime);
    }

    // Apply P&L filter - only show entries that are part of visible pairs
    if (plFilter !== 'all') {
      data = data.filter(d => {
        // Check if this entry belongs to a visible pair
        return visiblePairs.some(p =>
          Math.abs(p.entryTime - d.timestamp) < 1000 && p.symbol === d.symbol
        );
      });
    }

    return data;
  }, [chartData, zoomLevel, displayMinTime, displayMaxTime, plFilter, visiblePairs]);

  const exitData = useMemo(() => {
    let data = chartData.filter(d => d.action === 'exit');

    // Apply zoom window filter
    if (zoomLevel > 1) {
      data = data.filter(d => d.timestamp >= displayMinTime && d.timestamp <= displayMaxTime);
    }

    // Apply P&L filter - only show exits that are part of visible pairs
    if (plFilter !== 'all') {
      data = data.filter(d => {
        // Check if this exit belongs to a visible pair
        return visiblePairs.some(p =>
          Math.abs(p.exitTime - d.timestamp) < 1000 && p.symbol === d.symbol
        );
      });
    }

    return data;
  }, [chartData, zoomLevel, displayMinTime, displayMaxTime, plFilter, visiblePairs]);

  // Calculate domain for axes based on visible data
  const visibleData = [...entryData, ...exitData];
  const allPrices = visibleData.map(d => d.price);
  const minPrice = allPrices.length > 0 ? Math.min(...allPrices) * 0.95 : 0;
  const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) * 1.05 : 100;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      if (!data.symbol) return null;

      const isEntry = data.action === 'entry';

      // Find if this point is part of a pair
      const relatedPair = calculatedPairs.find(p =>
        (isEntry && Math.abs(p.entryTime - data.timestamp) < 1000 && p.symbol === data.symbol) ||
        (!isEntry && Math.abs(p.exitTime - data.timestamp) < 1000 && p.symbol === data.symbol)
      );

      return (
        <div style={{
          background: 'white',
          border: `2px solid ${data.color}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: '160px'
        }}>
          <div style={{
            fontWeight: 'bold',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isEntry ? data.color : 'white',
              border: `3px solid ${data.color}`
            }}></span>
            <span style={{ color: data.color, fontWeight: 'bold' }}>
              {data.symbol}
            </span>
            <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>
              {isEntry ? 'ENTRY' : 'EXIT'}
            </span>
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            <div>Price: <strong>${data.price.toFixed(2)}</strong></div>
            <div>Quantity: <strong>{data.quantity}</strong></div>
            <div>Date: {data.date}</div>
            <div>Time: {data.time}</div>
            {/* Only show P&L for Exit points */}
            {!isEntry && relatedPair && (
              <div style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid #eee',
                color: relatedPair.profit >= 0 ? '#22c55e' : '#ef4444',
                fontWeight: 'bold'
              }}>
                P&L: {relatedPair.profit >= 0 ? '+' : ''}${relatedPair.profit.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Format X-axis date
  const formatXAxis = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' });
  };

  // Custom dot for scatter points with labels
  const renderEntryDot = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy || isNaN(cx) || isNaN(cy)) return null;

    const color = payload.color;
    // Logarithmic scaling: better differentiation across 1-1000 range
    const size = 12 + Math.log10(Math.max(payload.quantity, 1)) * 8;
    const isDimmed = shouldDimSymbol(payload.symbol);

    return (
      <g key={`entry-${payload.id || payload.timestamp}`}>
        <circle
          cx={cx}
          cy={cy}
          r={size}
          fill={color}
          stroke={color}
          strokeWidth={2}
          opacity={isDimmed ? 0.15 : 0.9}
          style={{ cursor: 'pointer' }}
        />
        <text
          x={cx}
          y={cy - size - 6}
          textAnchor="middle"
          fill={color}
          fontSize={11}
          fontWeight="bold"
          opacity={isDimmed ? 0.15 : 1}
        >
          {payload.symbol}
        </text>
      </g>
    );
  };

  const renderExitDot = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy || isNaN(cx) || isNaN(cy)) return null;

    const stockColor = payload.color;
    // Logarithmic scaling: better differentiation across 1-1000 range
    const size = 12 + Math.log10(Math.max(payload.quantity, 1)) * 8;
    const isDimmed = shouldDimSymbol(payload.symbol);

    // Find related pair to determine P&L color
    const relatedPair = calculatedPairs.find(p =>
      Math.abs(p.exitTime - payload.timestamp) < 1000 && p.symbol === payload.symbol
    );
    const profit = relatedPair?.profit || 0;
    const plColor = profit >= 0 ? '#22c55e' : '#ef4444'; // Green for profit, Red for loss

    return (
      <g key={`exit-${payload.id || payload.timestamp}`}>
        <circle
          cx={cx}
          cy={cy}
          r={size}
          fill={stockColor}
          stroke={plColor}
          strokeWidth={4}
          opacity={isDimmed ? 0.15 : 0.9}
          style={{ cursor: 'pointer' }}
        />
        <text
          x={cx}
          y={cy - size - 6}
          textAnchor="middle"
          fill={stockColor}
          fontSize={11}
          fontWeight="bold"
          opacity={isDimmed ? 0.15 : 1}
        >
          {payload.symbol}
        </text>
      </g>
    );
  };

  // Calculate P&L stats for filter buttons (must be before conditional return)
  const plStats = useMemo(() => {
    const profitCount = calculatedPairs.filter(p => p.profit > 0).length;
    const lossCount = calculatedPairs.filter(p => p.profit < 0).length;
    return { profitCount, lossCount, total: calculatedPairs.length };
  }, [calculatedPairs]);

  if (chartData.length === 0) {
    return (
      <div style={{
        height: 450,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No data to display. Try adjusting your filters.
      </div>
    );
  }

  // Get unique symbols for legend
  const uniqueSymbols = [...new Set(records.map(r => r.symbol))];

  // Format date for X-axis label
  const formatDateRange = () => {
    const startDate = new Date(displayMinTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDate = new Date(displayMaxTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    return `${startDate} - ${endDate}`;
  };

  return (
    <div style={{ width: '100%', height: 500 }}>
      {/* Toolbar: P&L Filter + Zoom indicator */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        padding: '8px 12px',
        background: '#f8f9fa',
        borderRadius: '8px'
      }}>
        {/* P&L Filter Buttons */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>Filter:</span>
          <button
            onClick={() => setPlFilter('all')}
            style={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: 'none',
              background: plFilter === 'all' ? '#6366f1' : '#e5e7eb',
              color: plFilter === 'all' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px'
            }}
          >
            All ({plStats.total})
          </button>
          <button
            onClick={() => setPlFilter('profit')}
            style={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: 'none',
              background: plFilter === 'profit' ? '#22c55e' : '#e5e7eb',
              color: plFilter === 'profit' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px'
            }}
          >
            Profit ({plStats.profitCount})
          </button>
          <button
            onClick={() => setPlFilter('loss')}
            style={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: 'none',
              background: plFilter === 'loss' ? '#ef4444' : '#e5e7eb',
              color: plFilter === 'loss' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px'
            }}
          >
            Loss ({plStats.lossCount})
          </button>
        </div>

        {/* Zoom indicator */}
        {zoomLevel > 1 && (
          <div
            onClick={() => {
              setZoomLevel(1);
              setCenterTime(null);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: '#6366f1',
              color: 'white',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
            title="Click to reset zoom"
          >
            <span>🔍 {ZOOM_LEVELS[zoomLevel - 1].label}</span>
            <span style={{ opacity: 0.8 }}>✕</span>
          </div>
        )}
      </div>

      {/* Chart with SVG overlay for connection lines */}
      <div
        ref={chartRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        style={{
          position: 'relative',
          width: '100%',
          height: 360,
          cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none'
        }}
      >
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              type="number"
              dataKey="timestamp"
              name="Date"
              domain={[displayMinTime, displayMaxTime]}
              tickFormatter={formatXAxis}
              stroke="#666"
              fontSize={12}
              label={{ value: formatDateRange(), position: 'bottom', offset: 0 }}
            />
            <YAxis
              type="number"
              dataKey="price"
              name="Price"
              domain={[minPrice, maxPrice]}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
              stroke="#666"
              fontSize={12}
            />
            <ZAxis
              type="number"
              dataKey="quantity"
              range={[200, 800]}
              name="Quantity"
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Connection lines using Customized component */}
            <Customized
              component={({ xAxisMap, yAxisMap }) => {
                const xAxis = xAxisMap && Object.values(xAxisMap)[0];
                const yAxis = yAxisMap && Object.values(yAxisMap)[0];
                if (!xAxis || !yAxis) return null;

                return (
                  <g>
                    {visiblePairs.map((pair, idx) => {
                      const x1 = xAxis.scale(pair.entryTime);
                      const y1 = yAxis.scale(pair.entryPrice);
                      const x2 = xAxis.scale(pair.exitTime);
                      const y2 = yAxis.scale(pair.exitPrice);

                      if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) return null;

                      const isDimmed = shouldDimSymbol(pair.symbol);

                      return (
                        <line
                          key={`line-${idx}`}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke={pair.color}
                          strokeWidth={1.5}
                          strokeDasharray="4,4"
                          opacity={isDimmed ? 0.1 : 0.6}
                        />
                      );
                    })}
                  </g>
                );
              }}
            />

            {/* Entry points - Solid dots */}
            <Scatter
              name="Entry"
              data={entryData}
              shape={renderEntryDot}
            />

            {/* Exit points - Hollow dots */}
            <Scatter
              name="Exit"
              data={exitData}
              shape={renderExitDot}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Stock Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #e0e0e0',
        flexWrap: 'wrap'
      }}>
        <span style={{ fontWeight: '600', color: '#333', fontSize: '13px' }}>Stocks:</span>
        {uniqueSymbols.map(symbol => (
          <span
            key={symbol}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: focusedSymbols.has(symbol) ? symbolColors[symbol] : '#666',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              background: focusedSymbols.has(symbol) ? `${symbolColors[symbol]}15` : 'transparent',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={() => setHoveredSymbol(symbol)}
            onMouseLeave={() => setHoveredSymbol(null)}
            onClick={() => {
              setFocusedSymbols(prev => {
                const next = new Set(prev);
                if (next.has(symbol)) {
                  next.delete(symbol);
                } else {
                  next.add(symbol);
                }
                return next;
              });
            }}
          >
            <span style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: symbolColors[symbol]
            }}></span>
            {symbol}
          </span>
        ))}
        {focusedSymbols.size > 0 && (
          <span
            style={{
              fontSize: '12px',
              color: '#999',
              cursor: 'pointer'
            }}
            onClick={() => setFocusedSymbols(new Set())}
          >
            Clear filter
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6366f1' }}>
          🖱️ Scroll to zoom {zoomLevel > 1 ? '| Drag to pan' : ''}
        </span>
      </div>
    </div>
  );
};

export default TimelineChart;
